import { useEffect } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateDesignation, useUpdateDesignation } from '../../hooks/use-designations';
import {
  designationFormSchema,
  DesignationFormValues,
} from '../../schemas/designation-form-schema';
import { Designation } from '../../types/designation.types';

export interface DesignationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  designation?: Designation | null;
}

export const DesignationFormDialog = ({
  open,
  onOpenChange,
  designation,
}: DesignationFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(designation);

  const { mutateAsync: createDesignation, isPending: isCreating } = useCreateDesignation();
  const { mutateAsync: updateDesignation, isPending: isUpdating } = useUpdateDesignation();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<DesignationFormValues>({
    resolver: async (data, context, options) => {
      const result = await (
        zodResolver(designationFormSchema) as unknown as Resolver<DesignationFormValues>
      )(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Title: '', Weight: 1 },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      Title: designation?.Title ?? '',
      Weight: designation?.Weight ?? 1,
    });
  }, [open, designation, form]);

  const handleSubmit = async (values: DesignationFormValues) => {
    try {
      if (isEditMode && designation) {
        await updateDesignation({ itemId: designation.ItemId, input: values });
      } else {
        await createDesignation(values);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_DESIGNATION') : t('CREATE_DESIGNATION')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="Title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DESIGNATION_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('DESIGNATION_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Weight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('WEIGHT')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      placeholder={t('WEIGHT')}
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
